import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Link2, Users, LayoutDashboard, PlayCircle, Mail, FileBadge, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/me", label: "Dashboard", icon: LayoutDashboard, testId: "nav-me-dashboard" },
  { to: "/me/students", label: "My Students", icon: Users, testId: "nav-me-students" },
  { to: "/me/link", label: "My Link", icon: Link2, testId: "nav-me-link" },
  { to: "/me/inbox", label: "Inbox", icon: Mail, testId: "nav-me-inbox" },
  { to: "/me/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-me-sample-report" },
  { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-me-demo" },
];

export default function MarketingInbox() {
  const [messages, setMessages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = () => api.get("/marketing/inbox").then((res) => setMessages(res.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/marketing/inbox/${id}/read`);
    load();
  };

  const confirmDelete = async () => {
    try {
      await api.post(`/marketing/inbox/${deleteTarget}/delete`);
      toast.success("Message deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete message");
    }
    setDeleteTarget(null);
    load();
  };

  return (
    <DashboardShell links={LINKS} activePath="/me/inbox">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="marketing-inbox-heading">Inbox</h1>
      <p className="text-sm text-slate-500 mb-6">Messages sent by students who registered through your referral link.</p>
      <div className="space-y-3">
        {messages.map((m) => (
          <div key={m.message_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`me-inbox-message-${m.message_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-slate-900">{m.subject}</span>
                  {m.status === "NEW" && <Badge className="bg-indigo-600">New</Badge>}
                </div>
                <p className="text-xs text-slate-400">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {m.status === "NEW" && (
                  <Button size="sm" variant="outline" data-testid={`me-mark-read-button-${m.message_id}`} onClick={() => markRead(m.message_id)}>Mark as Read</Button>
                )}
                <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`me-delete-message-button-${m.message_id}`} onClick={() => setDeleteTarget(m.message_id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-3">{m.message}</p>
          </div>
        ))}
        {messages.length === 0 && <p className="text-slate-400 text-sm" data-testid="me-inbox-empty">No messages yet</p>}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="me-delete-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message permanently?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="me-delete-message-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="me-delete-message-confirm-button" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
